import { Coupon, ShopifyDiscountAffectsClass, ShopifyDiscountStatusEnum } from '@petplate/schema';
import { COUPON_SETTINGS_KEYS } from '@petplate/utils/coupons';
import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';
import { useCurrentUser } from './hooks/useCurrentUser';
import useURLCouponCode from './hooks/useURLCouponCode';
import { sdk } from './utils/sdk';
export type CouponSetting = {
  key: string;
  value: Partial<Coupon>;
};

// This should export only a list of coupons instead of an object - refactor when campaigns module is implemented
export const useSiteWideDiscounts = () => {
  try {
    const [resp, setResp] = useState<CouponSetting[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { currentUser } = useCurrentUser();

    const shouldApplySecondSubscriptionCoupon =
      currentUser && currentUser.numberOfSubscriptions === 1;
    const shouldApplySitewide = !currentUser || currentUser.numberOfSubscriptions === 0;

    const fetchSiteWideDiscounts = async () => {
      setIsLoading(true);
      try {
        const settingsData = await sdk.GetSettings({ vars: Object.values(COUPON_SETTINGS_KEYS) });

        const couponSettings =
          settingsData.settings?.map((s) => ({ key: s.key, value: s.value as Partial<Coupon> })) ||
          [];

        setResp(couponSettings);
      } catch (e) {
        captureException(e);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      fetchSiteWideDiscounts();
    }, []);

    const { coupons: urlCoupons, isLoadingURLCoupons } = useURLCouponCode();

    const validURLCoupons = urlCoupons.filter((c) => c.status === ShopifyDiscountStatusEnum.Active);
    let siteWideCoupons: Partial<Coupon>[] = [];

    if (validURLCoupons.length > 0) {
      siteWideCoupons = validURLCoupons.map((urlCoupon) => ({
        code: urlCoupon.title,
        percentOff: urlCoupon?.percentage || 0,
        amountOff: urlCoupon?.amount || 0,
        // for URL coupons we should always show the full code, since the user would have to know the code in the first place. It wouldn't make sense to mask the code in this case
        maskedCode: '',
        customPromoText: urlCoupon.customPromoText || '',
        inferredBannerText: urlCoupon.inferredBannerText || '',
        affectsClass: urlCoupon.affectsClass || ShopifyDiscountAffectsClass.Unknown,
        affectedProductSkus: urlCoupon.affectedProductSkus || [],
        affectedCollections: urlCoupon.affectedCollections || []
      }));
    } else if (shouldApplySecondSubscriptionCoupon) {
      const secondSubscriptionCoupon = resp.find(
        (s) => s.key === COUPON_SETTINGS_KEYS.SITE_WIDE_SECOND_SUBSCRIPTION_COUPON
      )?.value;
      if (secondSubscriptionCoupon) siteWideCoupons = [secondSubscriptionCoupon];
    } else if (shouldApplySitewide) {
      const siteWideCoupon = resp.find(
        (s) => s.key === COUPON_SETTINGS_KEYS.SITE_WIDE_COUPON
      )?.value;
      if (siteWideCoupon) siteWideCoupons = [siteWideCoupon];
    }

    const subCancelledEntreesDiscount = resp.find(
      (s) => s.key == COUPON_SETTINGS_KEYS.SUB_CANCELLED_ENTREES_DISCOUNT
    )?.value;
    const treatsDiscount = resp.find((s) => s.key === COUPON_SETTINGS_KEYS.TREATS_COUPON)?.value;

    return {
      siteWideCoupons,
      subCancelledEntreesDiscount,
      treatsDiscount,
      isLoading: isLoading || isLoadingURLCoupons
    };
  } catch (err) {
    console.error(err);
    captureException(err);

    return {
      siteWideCoupons: [],
      subCancelledEntreesDiscount: {},
      treatsDiscount: {},
      isLoading: false
    };
  }
};
